import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {ArrowLeft, ArrowRight, CheckCircle2, Loader2, AlertCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import PhoneInput, { type PaisOpcao, PAISES, validarNumero } from '@/components/PhoneInput'

const STATUS_OPCOES = [
  'Trabalhando com carteira assinada',
  'Autônoma',
  'Buscando uma nova oportunidade',
  'Sem renda no momento',
]

// Etapa 2 — Você gosta de compartilhar conhecimentos...
const COMPARTILHAR_OPCOES = [
  'Sim, totalmente',
  'Sim, mas tenho algumas dúvidas',
  'Ainda não tenho certeza',
]

// Etapa 3 — Você conhece os óleos essenciais?
const CONHECE_OLEOS_OPCOES = [
  'Nunca usei',
  'Já uso aleatoriamente',
  'Uso de forma periódica',
  'Tenho alguns mas não sei usar',
]

// Etapa 4 — Você já fez algum tipo de cadastro no site da dōTERRA?
const CADASTRO_DOTERRA_OPCOES = [
  'Nunca ouvi falar da dōTERRA',
  'Já ouvi falar mas nunca me cadastrei',
  'Já usei mas não sou cadastrada',
  'Sou cadastrada mas faz mais de 6 meses que não compro',
]

function Selecao() {
  const navigate = useNavigate()
  const [etapa, setEtapa] = useState(1)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [jaInscrita, setJaInscrita] = useState(false)
  const [indicadoPor, setIndicadoPor] = useState('')
  const [origem, setOrigem] = useState('Capacitacao (negocio)')

  // Etapa 1 — dados pessoais
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [paisSelecionado, setPaisSelecionado] = useState<PaisOpcao>(PAISES[0])
  const [status, setStatus] = useState('')

  // Etapa 2 — compartilhar conhecimentos
  const [respostaCompartilhar, setRespostaCompartilhar] = useState('')

  // Etapa 3 — conhece os óleos
  const [respostaConheceOleos, setRespostaConheceOleos] = useState('')

  // Etapa 4 — cadastro na dōTERRA
  const [respostaCadastroDoterra, setRespostaCadastroDoterra] = useState('')

  // Etapa 5 — declaração
  const [declaracaoAceita, setDeclaracaoAceita] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const indicado = params.get('indicado_por')
    if (indicado && indicado.trim()) {
      setIndicadoPor(indicado.trim())
    } else {
      try {
        const stored = sessionStorage.getItem('capacitacao_indicado_por')
        if (stored) setIndicadoPor(stored)
      } catch {
        // ignore
      }
    }

    const origensValidas = ['Capacitacao (negocio)', 'Capacitacao Equipe', 'Curso']
    const origemParam = params.get('origem')
    if (origemParam && origensValidas.includes(origemParam.trim())) {
      const valor = origemParam.trim()
      setOrigem(valor)
      try {
        sessionStorage.setItem('capacitacao_origem', valor)
      } catch {
        // ignore
      }
    } else {
      try {
        const storedOrigem = sessionStorage.getItem('capacitacao_origem')
        if (storedOrigem && origensValidas.includes(storedOrigem)) setOrigem(storedOrigem)
      } catch {
        // ignore
      }
    }
  }, [])

  function avancar() {
    setErro('')

    if (etapa === 1) {
      if (!nome.trim() || !email.trim() || !whatsapp.trim() || !status) {
        setErro('Por favor, preencha todos os campos.')
        return
      }
      if (!email.includes('@') || !email.includes('.')) {
        setErro('Informe um e-mail válido.')
        return
      }
      const validacao = validarNumero(whatsapp, paisSelecionado)
      if (!validacao.valido) {
        setErro(validacao.mensagem)
        return
      }
      setEtapa(2)
      return
    }
    if (etapa === 2) {
      if (!respostaCompartilhar) { setErro('Selecione uma opção para continuar.'); return }
      setEtapa(3)
      return
    }
    if (etapa === 3) {
      if (!respostaConheceOleos) { setErro('Selecione uma opção para continuar.'); return }
      setEtapa(4)
      return
    }
    if (etapa === 4) {
      if (!respostaCadastroDoterra) { setErro('Selecione uma opção para continuar.'); return }
      setEtapa(5)
    }
  }

  function voltar() {
    setErro('')
    if (etapa > 1) setEtapa(etapa - 1)
  }

  async function enviar() {
    setErro('')
    if (!declaracaoAceita) {
      setErro('Você precisa concordar com a declaração para continuar.')
      return
    }

    const validacaoWpp = validarNumero(whatsapp, paisSelecionado)
    if (!validacaoWpp.valido) {
      setErro(validacaoWpp.mensagem)
      setEtapa(1)
      return
    }

    setLoading(true)
    try {
      const digitsOnly = whatsapp.replace(/\D/g, '')
      const whatsappFormatado = paisSelecionado.codigo
        ? `${paisSelecionado.codigo}${digitsOnly}`
        : whatsapp.trim()

      const existentes = await lumi.entities.capacitacao.list({
        filter: { whatsapp: whatsappFormatado },
        limit: 1,
      })

      if (existentes?.list?.length > 0) {
        setJaInscrita(true)
        setLoading(false)
        return
      }

      const agora = new Date().toISOString()
      const dataFimAcesso = new Date(Date.now() + 1000 * 60 * 60 * 24 * 21).toISOString()
      const tokenAcesso = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`
      const tokenAcessoExpiracao = dataFimAcesso

      const dados: Record<string, unknown> = {
        nome: nome.trim(),
        email: email.trim().toLowerCase(),
        whatsapp: whatsappFormatado,
        status,
        // Respostas das etapas de seleção
        respostaCompartilhar,
        respostaConheceOleos,
        respostaCadastroDoterra,
        declaracaoAceita: true,
        dataInscricao: agora,
        dataInicioAcesso: agora,
        dataFimAcesso,
        tokenAcesso,
        tokenAcessoExpiracao,
        etapaAtual: 'selecao_concluida',
        indicadoPor: indicadoPor || 'monica',
        origem,
        statusInscricao: 'Inscrita',
        diagnosticoPreenchido: false,
        aulasCompletas: [],
        certificadoEmitido: false,
        senhaCriada: false,
        creator: 'public',
        createdAt: agora,
        updatedAt: agora,
      }

      // Gerar usuário único automaticamente
      const primeiroNomeNorm = nome.trim().split(/\s+/)[0] || 'usuario'
      const baseUsuario = primeiroNomeNorm
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20) || 'usuario'

      let usuarioGerado = baseUsuario
      let sufixo = 2
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const checkUsuario = await lumi.entities.consultoras.list({
          filter: { usuario: usuarioGerado },
          limit: 1,
        })
        if (!checkUsuario?.list?.length) break
        usuarioGerado = `${baseUsuario}${sufixo}`
        sufixo++
        if (sufixo > 999) break
      }

      const dadosComUsuario = { ...dados, usuario: usuarioGerado }
      const novoRegistro = await lumi.entities.capacitacao.create(dadosComUsuario)

      const agora2 = new Date().toISOString()
      const dataExpiracaoConsultora = new Date(Date.now() + 1000 * 60 * 60 * 24 * 21).toISOString()
      try {
        await lumi.entities.consultoras.create({
          nome: nome.trim(),
          usuario: usuarioGerado,
          senha: '',
          email: email.trim().toLowerCase(),
          whatsapp: whatsappFormatado,
          pais: paisSelecionado.nome || 'Brasil',
          ativo: false,
          tipoAcesso: 'capacitacao',
          statusAssinatura: 'capacitacao',
          dataExpiracao: dataExpiracaoConsultora,
          cursoConcluido: false,
          exibirOleosResultado: false,
          exibirOleosResultadoKids: false,
          creator: 'public',
          createdAt: agora2,
          updatedAt: agora2,
        })
      } catch (eConsultora) {
        console.warn('[selecao] aviso ao criar consultora prévia:', eConsultora)
      }

      try {
        const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'https://api.lumi.new'
        const projectId = (import.meta as any).env?.VITE_PROJECT_ID || 'p427216140118241280'
        await fetch(`${apiBase}/v1/functions/${projectId}/send-welcome-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'curso_inscricao_confirmada',
            nome: nome.trim(),
            email: email.trim().toLowerCase(),
            tokenAcesso,
          }),
          keepalive: true,
        })
        console.log('[curso] E-mail 1 (inscrição confirmada) disparado')
      } catch (e) {
        console.warn('[curso] erro ao enviar E-mail 1', e)
      }

      try {
        const id = (novoRegistro as any)?._id || (novoRegistro as any)?.id || ''
        sessionStorage.setItem('capacitacao_id', id)
        sessionStorage.setItem('capacitacao_whatsapp', whatsappFormatado)
        sessionStorage.setItem('capacitacao_nome', nome.trim())
        sessionStorage.setItem('capacitacao_email', email.trim())
        sessionStorage.setItem('capacitacao_token', tokenAcesso)
        sessionStorage.setItem('capacitacao_usuario', usuarioGerado)
      } catch {
        // ignore
      }

      navigate('/curso-aulas')
    } catch (e) {
      console.error('[selecao] erro ao salvar inscrição', e)
      setErro('Não foi possível enviar sua inscrição. Tente novamente em instantes.')
    } finally {
      setLoading(false)
    }
  }

  // Tela: já inscrita
  if (jaInscrita) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 sm:p-10 border border-[#EDE0D4] shadow-sm text-center">
          <CheckCircle2 className="mx-auto text-[#52B788] mb-4" size={48} />
          <h2 className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#2C2C2A] mb-3">
            Você já tem uma inscrição em andamento
          </h2>
          <p className="text-[#5F5E5A] mb-7 leading-relaxed">
            Continue de onde parou e finalize sua jornada no curso introdutório.
          </p>
          <Link
            to="/curso-aulas"
            className="inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white font-medium px-7 py-3.5 rounded-full hover:bg-[#1f4f3a] transition-colors w-full sm:w-auto"
          >
            Continuar de onde parei
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    )
  }

  const totalEtapas = 5
  const progresso = Math.round((etapa / totalEtapas) * 100)

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Barra de progresso */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#EDE0D4]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <span className="text-xs sm:text-sm font-medium text-[#5F5E5A]">
            Etapa {etapa} de {totalEtapas}
          </span>
          <div className="flex-1 h-1.5 bg-[#EDE0D4] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#52B788] transition-all duration-500"
              style={{ width: `${progresso}%` }}
            />
          </div>
          <span className="text-xs sm:text-sm font-medium text-[#2D6A4F] tabular-nums">{progresso}%</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-[#EDE0D4] shadow-sm">

          {/* Etapa 1 — dados pessoais */}
          {etapa === 1 && (
            <div>
              <span className="block italic text-sm text-[#B08968] mb-2">Vamos começar</span>
              <h2 className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#2C2C2A] mb-2">
                Conte um pouco sobre você
              </h2>
              <p className="text-[#5F5E5A] text-sm sm:text-base mb-6">
                Esses dados são confidenciais e usados apenas no processo seletivo.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">Nome completo</label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#EDE0D4] bg-white text-[#2C2C2A] focus:outline-none focus:border-[#52B788] transition-colors"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">E-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#EDE0D4] bg-white text-[#2C2C2A] focus:outline-none focus:border-[#52B788] transition-colors"
                    placeholder="seuemail@exemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">WhatsApp</label>
                  <PhoneInput
                    value={whatsapp}
                    onChange={(numero, pais) => {
                      setWhatsapp(numero)
                      setPaisSelecionado(pais)
                    }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2A] mb-2">Status atual</label>
                  <div className="space-y-2">
                    {STATUS_OPCOES.map((op) => (
                      <label
                        key={op}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                          status === op
                            ? 'border-[#52B788] bg-[#D8F3DC]'
                            : 'border-[#EDE0D4] bg-white hover:border-[#52B788]/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="status"
                          value={op}
                          checked={status === op}
                          onChange={() => setStatus(op)}
                          className="accent-[#2D6A4F]"
                        />
                        <span className="text-sm sm:text-base text-[#2C2C2A]">{op}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Etapa 2 — Compartilhar conhecimentos */}
          {etapa === 2 && (
            <RadioStep
              label="Pergunta 1 de 3"
              titulo="Você gosta de compartilhar conhecimentos, gerar transformações na vida das pessoas e se vê numa postura de aprender como se conectar com as pessoas de forma simples e estratégica?"
              opcoes={COMPARTILHAR_OPCOES}
              valor={respostaCompartilhar}
              onChange={setRespostaCompartilhar}
            />
          )}

          {/* Etapa 3 — Conhece os óleos */}
          {etapa === 3 && (
            <RadioStep
              label="Pergunta 2 de 3"
              titulo="Você conhece os óleos essenciais?"
              opcoes={CONHECE_OLEOS_OPCOES}
              valor={respostaConheceOleos}
              onChange={setRespostaConheceOleos}
            />
          )}

          {/* Etapa 4 — Cadastro na dōTERRA */}
          {etapa === 4 && (
            <RadioStep
              label="Pergunta 3 de 3"
              titulo="Você já fez algum tipo de cadastro no site da dōTERRA?"
              opcoes={CADASTRO_DOTERRA_OPCOES}
              valor={respostaCadastroDoterra}
              onChange={setRespostaCadastroDoterra}
            />
          )}

          {/* Etapa 5 — Declaração */}
          {etapa === 5 && (
            <div>
              <span className="block italic text-sm text-[#B08968] mb-2">Última etapa</span>
              <h2 className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#2C2C2A] mb-5">
                Quase lá!
              </h2>

              <div className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-2xl p-5 mb-5">
                <p className="text-sm sm:text-base text-[#2C2C2A] leading-relaxed">
                  Ao se inscrever você declara que não é consultor(a) ativo(a) da dōTERRA. Esta seleção é uma oportunidade exclusiva para pessoas que ainda não fazem parte da marca.
                </p>
                <p className="text-sm sm:text-base text-[#2C2C2A] leading-relaxed mt-3">
                  Caso identificarmos divergência nas informações o acesso será encerrado automaticamente.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={declaracaoAceita}
                  onChange={(e) => setDeclaracaoAceita(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#2D6A4F]"
                />
                <span className="text-sm sm:text-base text-[#2C2C2A] leading-relaxed">
                  Declaro que as informações acima são verdadeiras
                </span>
              </label>
            </div>
          )}

          {erro && (
            <div className="mt-5 px-4 py-3 rounded-xl bg-[#FEE2E2] border border-[#F87171] text-[#991B1B] text-sm flex items-start gap-2">
              <AlertCircle className="shrink-0 mt-0.5" size={16} />
              <span>{erro}</span>
            </div>
          )}

          <div className="mt-7 flex items-center justify-between gap-3">
            {etapa > 1 ? (
              <button
                type="button"
                onClick={voltar}
                className="inline-flex items-center gap-2 text-sm text-[#5F5E5A] hover:text-[#2D6A4F] transition-colors"
              >
                <ArrowLeft size={16} />
                Voltar
              </button>
            ) : (
              <Link
                to="/capacitacao"
                className="inline-flex items-center gap-2 text-sm text-[#5F5E5A] hover:text-[#2D6A4F] transition-colors"
              >
                <ArrowLeft size={16} />
                Voltar
              </Link>
            )}

            {etapa < 5 && (
              <button
                type="button"
                onClick={avancar}
                disabled={etapa === 1 && !validarNumero(whatsapp, paisSelecionado).valido}
                className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 sm:px-7 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Continuar
                <ArrowRight size={18} />
              </button>
            )}

            {etapa === 5 && (
              <button
                type="button"
                onClick={enviar}
                disabled={loading || !declaracaoAceita}
                className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 sm:px-7 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Enviando...
                  </>
                ) : (
                  <>
                    Quero participar da seleção
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function RadioStep({
  label,
  titulo,
  opcoes,
  valor,
  onChange,
}: {
  label: string
  titulo: string
  opcoes: string[]
  valor: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <span className="block italic text-sm text-[#B08968] mb-2">{label}</span>
      <h2 className="font-cormorant text-xl sm:text-2xl md:text-3xl font-semibold text-[#2C2C2A] leading-snug mb-5">
        {titulo}
      </h2>
      <div className="space-y-2">
        {opcoes.map((op) => (
          <label
            key={op}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border cursor-pointer transition-all ${
              valor === op
                ? 'border-[#52B788] bg-[#D8F3DC]'
                : 'border-[#EDE0D4] bg-white hover:border-[#52B788]/50'
            }`}
          >
            <input
              type="radio"
              name="opcao"
              value={op}
              checked={valor === op}
              onChange={() => onChange(op)}
              className="accent-[#2D6A4F]"
            />
            <span className="text-sm sm:text-base text-[#2C2C2A]">{op}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

export default Selecao
